import { requestSystemSettingByKey } from '@/graphql/queries/admin';

export interface OpenRouterModel {
  id: string;
  name: string;
  description?: string;
  context_length: number;
  pricing: {
    prompt: string;
    completion: string;
  };
  architecture?: {
    modality: string;
    input_modalities?: string[];
    output_modalities?: string[];
  };
  top_provider?: {
    max_completion_tokens?: number;
  };
}

export interface TransformedModel {
  id: string;
  name: string;
  description?: string;
  contextLength: number;
  maxTokens?: number;
  promptCost: number;
  completionCost: number;
  provider: string;
  isRecommended?: boolean;
}

const RECOMMENDED_MODELS: Record<string, string[]> = {
  documentGeneration: [
    'anthropic/claude-3.5-sonnet',
    'anthropic/claude-sonnet-4',
    'openai/gpt-4o',
    'google/gemini-2.0-flash-001',
  ],
  formEnrichment: [
    'meta-llama/llama-4-scout:nitro',
    'anthropic/claude-3.5-sonnet',
    'openai/gpt-4o-mini',
    'google/gemini-2.0-flash-001',
  ],
  dynamicForm: [
    'meta-llama/llama-4-scout:nitro',
    'anthropic/claude-3.5-sonnet',
    'openai/gpt-4o-mini',
  ],
};

function isUsableModel(model: OpenRouterModel): boolean {
  const outputModalities = model.architecture?.output_modalities || [];
  if (!outputModalities.includes('text')) return false;
  if (model.context_length < 4000) return false;
  const modality = model.architecture?.modality || '';
  if (modality.includes('image->') && !modality.includes('text')) return false;
  if (modality.includes('audio->') && !modality.includes('text')) return false;
  return true;
}

function transformModel(model: OpenRouterModel, useCase?: string): TransformedModel {
  const provider = model.id.split('/')[0];
  const recommendedList = useCase ? RECOMMENDED_MODELS[useCase] || [] : [];

  return {
    id: model.id,
    name: model.name,
    description: model.description,
    contextLength: model.context_length,
    maxTokens: model.top_provider?.max_completion_tokens,
    promptCost: parseFloat(model.pricing.prompt) * 1000,
    completionCost: parseFloat(model.pricing.completion) * 1000,
    provider,
    isRecommended: recommendedList.includes(model.id),
  };
}

// In-memory cache
let cachedModels: OpenRouterModel[] | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

export async function fetchOpenRouterModels(
  useCase?: string,
  forceRefresh = false,
): Promise<TransformedModel[]> {
  const now = Date.now();

  // Return cached if valid
  if (!forceRefresh && cachedModels && now - cacheTimestamp < CACHE_DURATION_MS) {
    return cachedModels
      .filter(isUsableModel)
      .map((m) => transformModel(m, useCase))
      .sort(sortModels);
  }

  // Get API key from system settings, fallback to env
  const setting = await requestSystemSettingByKey('openRouterApiKey');
  const apiKey = setting?.Value || import.meta.env.VITE_OPENROUTER_API_KEY || '';

  if (!apiKey) {
    console.warn('[OpenRouter] No API key configured');
    return [];
  }

  const response = await fetch('https://openrouter.ai/api/v1/models', {
    headers: { Authorization: `Bearer ${apiKey}` },
  });

  if (!response.ok) {
    console.error('[OpenRouter] Failed to fetch models:', response.statusText);
    throw new Error('Failed to fetch models from OpenRouter');
  }

  const data = await response.json();
  cachedModels = data.data as OpenRouterModel[];
  cacheTimestamp = now;

  return cachedModels
    .filter(isUsableModel)
    .map((m) => transformModel(m, useCase))
    .sort(sortModels);
}

function sortModels(a: TransformedModel, b: TransformedModel): number {
  if (a.isRecommended && !b.isRecommended) return -1;
  if (!a.isRecommended && b.isRecommended) return 1;
  if (a.provider !== b.provider) return a.provider.localeCompare(b.provider);
  return a.name.localeCompare(b.name);
}
